import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useTheme } from '@styledComponents';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';

import { skewHWRatio } from '@sections/online-rsvp/FindPerfectInvitationSection/FindPerfectInvitationSection.styles';

export const useFindPerfectInvitationSectionController = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);
  const invitation1BoxRef = useRef<HTMLDivElement>(null);
  const invitation2BoxRef = useRef<HTMLDivElement>(null);
  const invitation3BoxRef = useRef<HTMLDivElement>(null);
  const invitation4BoxRef = useRef<HTMLDivElement>(null);
  const invitation5BoxRef = useRef<HTMLDivElement>(null);
  const invitation6BoxRef = useRef<HTMLDivElement>(null);
  //
  const invitationBoxesRefs = useMemo(
    () => [invitation1BoxRef, invitation2BoxRef, invitation3BoxRef, invitation4BoxRef, invitation5BoxRef, invitation6BoxRef] as const,
    [invitation1BoxRef, invitation2BoxRef, invitation3BoxRef, invitation4BoxRef, invitation5BoxRef, invitation6BoxRef]
  );
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();
  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let sectionTimeline: ReturnType<typeof gsap.timeline>;
    const sectionEl = sectionRef.current;
    const invitationBoxElements = invitationBoxesRefs.map((ref) => ref.current);
    if (scrollController && isMounted && sectionEl) {
      const initTimeline = () => {
        sectionTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        invitationBoxElements
          .filter((el, index) => index > 0 && !!el)
          .forEach((el, index) => {
            sectionTimeline
              .to(
                el,
                {
                  clipPath: `polygon(0% -${100 * skewHWRatio}%, 100% 0%, 100% 100%, 0% 100%)`,
                  duration: 1,
                },
                index > 0 ? '>-0.6' : 0
              )
              .set(invitationBoxElements[index], { visibility: 'hidden' });
          });
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: sectionEl,
          triggerHook: 'onLeave',
          duration: '150%',
        })
          .setPin(sectionEl)
          // .addIndicators()
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            sectionTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (sectionTimeline) {
        sectionTimeline.progress(0);
        sectionTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted, sectionRef, invitationBoxesRefs, theme]);
  return {
    sectionRef,
    invitationBoxesRefs,
    isScrolledIntoView,
    setScrolledIntoView,
  };
};
